if (customElements.get('sticky-atc-base') === undefined) {
  class StickyAtcBase extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.updateStickyOffset = this.updateStickyOffset.bind(this);
      this.onDocChange = this.onDocChange.bind(this);
      this.ticking = false;
    }

    connectedCallback() {
      const productForm = document.querySelector('product-form, .product-form');
      this.mainAtc =
        (productForm && productForm.querySelector('[type="submit"][name="add"], .product-form__submit')) ||
        document.querySelector('.product-form__submit, [name="add"], .add-to-cart');
      if (!this.mainAtc) {
        if (typeof console !== 'undefined') console.warn('[sticky-atc-v2] no product-form ATC found — sticky bar will not show/hide on scroll');
        return;
      }

      // Sync the swatch/name with the main product form's variant selection.
      // Price is handled separately by global.js: renderProductInfo copies both the price and
      // the compare at price out of the re-rendered bar (it resolves them via
      // sticky-atc-base[data-section]), so this element does not write them.
      // Guarded: if pub/sub globals are missing we still want the scroll behaviour below,
      // rather than a constructor throw that aborts the whole element upgrade.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      } else {
        console.warn('[sticky-atc-v2] pub/sub unavailable — sticky swatch/name will not update on variant change');
      }

      this.shadeMap = {};
      try {
        this.shadeMap = JSON.parse(this.dataset.shadeMap || '{}');
      } catch (e) {
        // Malformed shade-map breaks sticky bar swatch updates — promote to error
        // so the silent UX failure (sticky swatch stays on initial color) is visible.
        if (typeof console !== 'undefined') console.error('[sticky-atc-v2] shade-map is malformed JSON — sticky swatch updates disabled:', e, this.dataset.shadeMap);
      }

      this.swatchEl = this.querySelector('[data-sticky-swatch]');
      this.shadeNameEl = this.querySelector('[data-sticky-shade-name]');

      // All four are absent when the section's sticky_show_shades setting is off;
      // every consumer below is null-guarded so only the title suffix keeps working.
      this.single = this.querySelector('[data-sticky-mode="single"]');
      this.multi = this.querySelector('[data-sticky-mode="multi"]');
      this.slotTemplate = this.querySelector('[data-sticky-slot-template]');
      this.titleEl = this.querySelector('.sticky-atc-v2__title');
      this.baseTitle = this.titleEl ? this.titleEl.textContent.trim() : '';

      this.headerEl =
        document.querySelector('sticky-header.header-wrapper') ||
        document.querySelector('.header-wrapper') ||
        document.querySelector('.shopify-section-header');

      this.updateStickyOffset();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.updateStickyOffset);

      this.io = new IntersectionObserver(
        ([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
          this.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
        },
        { threshold: 0 }
      );
      // Observes the node captured at connect time. Safe today: variant changes
      // toggle the ATC button in place (global.js renderProductInfo) and never
      // replace it. If a partial refresh ever swaps the form's DOM, re-observe.
      this.io.observe(this.mainAtc);

      // Delegated rather than bound to the picker: <concealer-variant> owns its own
      // element-level change handler, and a document-level listener runs after it —
      // so by the time this fires, aria-hidden already reflects the new visible picker.
      document.addEventListener('change', this.onDocChange);

      this.syncMode();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.updateStickyOffset);
      document.removeEventListener('change', this.onDocChange);
      if (this.io) this.io.disconnect();
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    }

    onVariantChange(evt) {
      const variant = evt && evt.data && evt.data.variant;
      if (!variant) return;
      // Scope to this section when the event carries a section id (some publishers omit it).
      if (evt.data.sectionId && this.dataset.section && evt.data.sectionId !== this.dataset.section) return;

      if (this.shadeNameEl) this.shadeNameEl.textContent = variant.title;
      if (this.swatchEl) {
        const hex = this.normalizeHex(this.shadeMap[variant.title]);
        if (hex) this.swatchEl.style.background = hex;
      }
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateStickyOffset();
        this.ticking = false;
      });
    }

    updateStickyOffset() {
      if (!this.headerEl) return;
      const bottom = Math.max(0, this.headerEl.getBoundingClientRect().bottom);
      this.style.setProperty('--sticky-atc-v2-offset', bottom + 'px');
    }

    normalizeHex(value) {
      if (!value) return '';
      const v = String(value).trim();
      return v.startsWith('#') ? v : '#' + v;
    }

    onDocChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      const name = target.name || '';
      if (
        name.indexOf('bundleItem[') === 0 ||
        name === 'concealer-amount' ||
        name === 'purchase_option' ||
        target.hasAttribute('data-amount-selector')
      ) {
        // One entry point for every trigger. Re-reading the picker is cheaper than
        // tracking which inputs invalidate what, and it can't go stale.
        this.syncMode();
      }
    }

    // The bundle picker the buy box is actually showing, or null on a PDP that has no
    // concealer picker at all. Deliberately the source of truth over the checked
    // concealer-amount radio: global.js switches the visible picker on a purchase-option
    // change WITHOUT moving that radio, and the shade row must never contradict the
    // shades the customer can see themselves choosing.
    getVisiblePicker() {
      return document.querySelector('.concealer-variant__picker[aria-hidden="false"]');
    }

    // Distinct bundleItem[] group names inside a picker, in DOM order. Length is the
    // slot count — nothing here assumes a tier size, so a picker that ever renders more
    // than three slots is followed automatically.
    getBundleGroups(picker) {
      if (!picker) return [];
      const names = [];
      picker.querySelectorAll('input[name^="bundleItem["]').forEach((input) => {
        if (input.name && names.indexOf(input.name) === -1) names.push(input.name);
      });
      return names;
    }

    // How many of the tier's slots are free, read off the tier matching `quantity`
    // rather than the checked radio — same desync reason as getVisiblePicker above.
    getFreeCount(quantity) {
      const tier = document.querySelector('input[name="concealer-amount"][value="' + quantity + '"]');
      if (!tier) return 0;
      const free = parseInt(tier.dataset.quantityFree, 10);
      if (isNaN(free) || free < 1) return 0;
      return Math.min(free, quantity);
    }

    // Drives the "x N" title suffix. Excludes purchase_option: those radios also carry
    // data-amount-selector but have no numeric value, and the plan tier is not a quantity.
    getSelectedQuantity() {
      const checked = document.querySelector('input[data-amount-selector]:checked:not([name="purchase_option"])');
      if (!checked) return 1;
      const quantity = parseInt(checked.value, 10);
      return isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    renderSlots(picker, groups) {
      if (!this.multi || !this.slotTemplate) return;

      const firstFreeIndex = groups.length - this.getFreeCount(groups.length);
      const fragment = document.createDocumentFragment();

      groups.forEach((groupName, index) => {
        const slot = this.slotTemplate.content.firstElementChild.cloneNode(true);
        const swatch = slot.querySelector('[data-sticky-multi-swatch]');
        const label = slot.querySelector('[data-sticky-multi-name]');
        const badge = slot.querySelector('[data-sticky-slot-free]');
        const chosen = picker.querySelector('input[name="' + groupName + '"]:checked');

        if (chosen) {
          const title = chosen.dataset.shadeTitle || chosen.value || '';
          // Same fallback the Liquid uses for the single swatch, so a variant missing
          // from the shade_color metaobject looks the same in both modes.
          const hex = this.normalizeHex(chosen.dataset.shadeHex || this.shadeMap[title]) || '#F5DFC5';
          if (swatch) swatch.style.background = hex;
          if (label) label.textContent = title;
        } else {
          // Slots 2+ start unchecked. Showing the default variant here (what
          // sticky-atc-aiw-v2 does) would claim a shade the customer never picked.
          slot.classList.add('slot--empty');
          if (label) label.textContent = 'Select';
        }

        if (badge) badge.hidden = index < firstFreeIndex;
        fragment.appendChild(slot);
      });

      this.multi.innerHTML = '';
      this.multi.appendChild(fragment);
    }

    // One unit per bundle slot when a concealer picker is on the page, because the
    // visible picker is this bar's source of truth everywhere else too. Following the
    // checked concealer-amount radio instead would print "x 3" beside a one-unit price
    // in exactly the desync global.js creates: choose Buy 3, then AutoShip, and the
    // buy box drops back to the buy-1 picker and a single-unit price while that radio
    // stays on 3. The radio is only consulted on PDPs with no picker to mirror.
    getTitleQuantity(groups) {
      if (document.querySelector('.concealer-variant__picker')) return groups.length || 1;
      return this.getSelectedQuantity();
    }

    updateTitle(groups) {
      if (!this.titleEl) return;
      // The suffix exists to explain a multiplied price, and on mobile — where the shade
      // row is display:none — it is the only sign that a bundle is selected at all.
      const quantity = this.getTitleQuantity(groups);
      this.titleEl.textContent = quantity > 1 ? this.baseTitle + ' \u00d7 ' + quantity : this.baseTitle;
    }

    syncMode() {
      const picker = this.getVisiblePicker();
      const groups = this.getBundleGroups(picker);
      const isMulti = groups.length > 1;

      if (this.single) this.single.hidden = isMulti;
      if (this.multi) {
        this.multi.hidden = !isMulti;
        if (isMulti) this.renderSlots(picker, groups);
        else this.multi.innerHTML = '';
      }

      this.updateTitle(groups);
    }
  }

  customElements.define('sticky-atc-base', StickyAtcBase);
}
